import { Link, useLocation } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { useAuthStore } from '../../contexts/authStore'
import { menuAPI, pagesAPI } from '../../services/api'
import BrandMark from './BrandMark'

import { logger } from '@/utils/logger';
export default function Header() {
  const { isAuthenticated, userType, user, logout } = useAuthStore()
  const [menuItems, setMenuItems] = useState([])
  const [pages, setPages] = useState({})
  const [openDropdown, setOpenDropdown] = useState(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const navRef = useRef(null)
  const location = useLocation()

  useEffect(() => {
    loadMenu()
    loadPages()
  }, [])

  // Beim Seitenwechsel alle Overlays schließen
  useEffect(() => {
    setMobileOpen(false)
    setUserMenuOpen(false)
    setOpenDropdown(null)
  }, [location.pathname])

  // Klick außerhalb der Navigation bzw. Escape schließt die Menüs
  useEffect(() => {
    const onClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null)
        setUserMenuOpen(false)
      }
    }
    const onEscape = (e) => {
      if (e.key === 'Escape') {
        setOpenDropdown(null)
        setUserMenuOpen(false)
        setMobileOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onEscape)
    }
  }, [])

  const loadMenu = async () => {
    try {
      const response = await menuAPI.getPublic()
      setMenuItems(response.data)
    } catch (error) {
      logger.error('Error loading menu:', error)
    }
  }

  const loadPages = async () => {
    try {
      const response = await pagesAPI.getAll()
      const pagesMap = {}
      response.data.forEach(page => {
        pagesMap[page.id] = page.slug
      })
      setPages(pagesMap)
    } catch (error) {
      logger.error('Error loading pages:', error)
    }
  }

  const getMenuUrl = (item) => {
    if (item.url) return item.url
    if (item.page_id && pages[item.page_id]) return `/${pages[item.page_id]}`
    return '#'
  }

  const isExternal = (url) => /^https?:\/\//i.test(url)

  const MenuLink = ({ item, className = '' }) => {
    const url = getMenuUrl(item)

    if (isExternal(url)) {
      return (
        <a
          href={url}
          target={item.open_in_new_tab ? '_blank' : undefined}
          rel={item.open_in_new_tab ? 'noopener noreferrer' : undefined}
          className={className}
        >
          {item.title}
        </a>
      )
    }

    return (
      <Link
        to={url}
        target={item.open_in_new_tab ? '_blank' : undefined}
        className={className}
        aria-current={location.pathname === url ? 'page' : undefined}
      >
        {item.title}
      </Link>
    )
  }

  const renderMenuItem = (item) => {
    const hasChildren = item.children && item.children.length > 0

    if (!hasChildren) {
      return <MenuLink key={item.id} item={item} />
    }

    const isOpen = openDropdown === item.id

    return (
      <div key={item.id} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setOpenDropdown(isOpen ? null : item.id)}
          aria-expanded={isOpen}
          aria-haspopup="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: 'transparent',
            border: 0,
            padding: 0,
            font: 'inherit',
            fontSize: 14,
            color: isOpen ? 'var(--wc-accent)' : 'inherit',
            cursor: 'pointer',
          }}
        >
          {item.title}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform .15s ease',
            }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div
            className="wc-card wc-elev-md wc-dropdown"
            style={{ left: 0, minWidth: 200 }}
          >
            {item.children.map(child => (
              <MenuLink key={child.id} item={child} className="wc-dropdown-link" />
            ))}
          </div>
        )}
      </div>
    )
  }

  const accountPath = userType === 'admin' ? '/admin/account' : '/customer/account'
  const panelPath = userType === 'admin' ? '/admin' : '/customer'

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        background: 'color-mix(in srgb, var(--wc-bg) 88%, transparent)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--wc-divider)',
      }}
    >
      <nav className="wc-nav wc-container" ref={navRef}>
        <Link
          to="/"
          className="wc-nav-brand"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            textDecoration: 'none',
            color: 'var(--wc-text)',
          }}
        >
          <BrandMark />
          Werkstatt-Connect
        </Link>

        <div
          className="wc-nav-links"
          style={{ display: 'flex', alignItems: 'center', gap: 28 }}
        >
          {menuItems.map(item => renderMenuItem(item))}
        </div>

        {!isAuthenticated ? (
          <Link to="/login" className="wc-btn wc-btn-secondary">
            Anmelden
          </Link>
        ) : (
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              aria-expanded={userMenuOpen}
              aria-haspopup="true"
              className="wc-btn wc-btn-secondary"
              style={{ gap: 10, paddingRight: 6 }}
            >
              <span
                className="wc-nav-links"
                style={{
                  maxWidth: 180,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.email}
              </span>
              <span
                aria-hidden="true"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: 'var(--wc-accent-800)',
                  color: 'var(--wc-accent-100)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  flex: 'none',
                }}
              >
                {user?.email?.charAt(0).toUpperCase()}
              </span>
            </button>

            {userMenuOpen && (
              <div
                className="wc-card wc-elev-md wc-dropdown"
                style={{ right: 0, minWidth: 240 }}
              >
                <div
                  style={{
                    padding: '8px 10px 10px',
                    borderBottom: '1px solid var(--wc-divider)',
                    marginBottom: 4,
                  }}
                >
                  <div className="wc-muted" style={{ fontSize: 11 }}>
                    Angemeldet als
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {user?.email}
                  </div>
                </div>
                <Link to={accountPath} className="wc-dropdown-link">
                  Mein Account
                </Link>
                <Link to={panelPath} className="wc-dropdown-link">
                  {userType === 'admin' ? 'Admin-Panel' : 'Kunden-Panel'}
                </Link>
                <button
                  type="button"
                  onClick={logout}
                  className="wc-dropdown-link wc-dropdown-danger"
                >
                  Abmelden
                </button>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          className="wc-btn wc-btn-secondary wc-btn-icon wc-nav-burger"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? 'Navigation schließen' : 'Navigation öffnen'}
          style={{ display: 'none' }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
          >
            {mobileOpen ? (
              <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
            ) : (
              <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
            )}
          </svg>
        </button>
      </nav>

      {mobileOpen && menuItems.length > 0 && (
        <div
          className="wc-container"
          style={{
            paddingTop: 10,
            paddingBottom: 18,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            borderTop: '1px solid var(--wc-divider)',
          }}
        >
          {menuItems.map(item => (
            <div key={item.id}>
              <MenuLink item={item} className="wc-dropdown-link" />
              {item.children?.map(child => (
                <MenuLink
                  key={child.id}
                  item={child}
                  className="wc-dropdown-link wc-dropdown-child"
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </header>
  )
}
